const OBJECT_ID_REGEX = /^[a-f0-9]{24}$/i;
const NUMERIC_ID_REGEX = /^\d+$/;

const normalizeText = (value) => String(value ?? "").trim();

export const isLikelyProductId = (value) => {
  const v = normalizeText(value);
  if (!v) return false;
  return NUMERIC_ID_REGEX.test(v) || OBJECT_ID_REGEX.test(v);
};

export const slugifyProductTitle = (value) => {
  const raw = normalizeText(value)
    .toLowerCase()
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/&/g, " and ");

  const slug = raw
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 96);

  return slug || "product";
};

export const getProductIdFromValue = (value) => {
  if (!value) return "";
  if (typeof value === "string" || typeof value === "number") {
    return normalizeText(value);
  }

  const candidate =
    value?.publicId ||
    value?.urlId ||
    value?.productCountryId ||
    value?.internalId ||
    value?._id ||
    value?.id ||
    value?.product?.publicId ||
    value?.product?.urlId ||
    value?.product?.productCountryId ||
    value?.product?.internalId ||
    value?.product?._id ||
    value?.product?.id ||
    value?.productId ||
    value?.product_id ||
    value?.product?.productId ||
    value?.product?.product_id ||
    "";

  return normalizeText(candidate);
};

export const getProductLookupIdFromValue = (value) => {
  if (!value) return "";
  if (typeof value === "string" || typeof value === "number") {
    return normalizeText(value);
  }

  const candidate =
    value?.productId ||
    value?.product_id ||
    value?.productID ||
    value?.logistic_product_id ||
    value?.product?.productId ||
    value?.product?.product_id ||
    value?.product?.productID ||
    value?.product?.id ||
    value?.product?._id ||
    value?.publicId ||
    value?.urlId ||
    value?.productCountryId ||
    value?.internalId ||
    value?._id ||
    value?.id ||
    "";

  return normalizeText(candidate);
};

export const buildProductRouteState = (value) => {
  const productLookupId = getProductLookupIdFromValue(value);
  return productLookupId ? { productLookupId } : undefined;
};

export const getProductTitleFromValue = (value) => {
  if (!value || typeof value !== "object") return "";
  return normalizeText(
    value?.name ||
      value?.title ||
      value?.subject ||
      value?.productName ||
      value?.ae_item_base_info_dto?.subject ||
      ""
  );
};

export const buildProductSlug = (value, fallbackTitle = "") => {
  const title = getProductTitleFromValue(value) || normalizeText(fallbackTitle) || "product";
  return slugifyProductTitle(title);
};

export const parseProductIdFromSlug = (value) => {
  const raw = normalizeText(decodeURIComponent(String(value ?? "")));
  if (!raw) return "";
  if (isLikelyProductId(raw)) return raw;

  const markerMatch = raw.match(/--([^/]+)$/);
  if (markerMatch && markerMatch[1] && isLikelyProductId(markerMatch[1])) {
    return markerMatch[1];
  }

  const trailingMatch = raw.match(/-([a-f0-9]{24}|\d+)$/i);
  if (trailingMatch && trailingMatch[1] && isLikelyProductId(trailingMatch[1])) {
    return trailingMatch[1];
  }

  return raw;
};

export const buildProductPath = (value, fallbackTitle = "", query = "") => {
  const slug = buildProductSlug(value, fallbackTitle);
  if (!slug) return "#";

  const suffix = query
    ? query.startsWith("?")
      ? query
      : `?${query}`
    : "";

  return `/products/${slug}${suffix}`;
};
